import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  HookFormCheckbox,
  HookFormCountrySelect,
  HookFormEmailInput,
  HookFormPasswordInput,
  HookFormTextInput,
} from '@realtyka/real-ui';
import { isEmpty } from 'lodash';
import qs from 'qs';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import SignUpLeoImg from '../assets/img/auth/signup-leo-prompt.png';
import DefaultSplashV2 from '../components/DefaultSplashV2.tsx';
import HomeRoute from '../components/Helmet/HomeRoute.tsx';
import AuthPageLayoutV2 from '../components/Layout/AuthPageLayoutV2.tsx';
import {
  REAL_SUPPORT_LINK,
  TC_ONEREAL_DATA_POLICY_LINK,
  TC_ONEREAL_ELECTRONIC_DISCLOSURE_LINK,
  TC_ONEREAL_PRIVACY_POLICY_LINK,
  TC_ONEREAL_TERMS_OF_USE_LINK,
} from '../constants/OneRealMortgageConstants';
import { useRefinement } from '../hooks/useRefinement.tsx';
import { AuthControllerApi, LoginRequest } from '../openapi/keymaker';
import {
  PublicControllerApi,
  SignUpAndOnboardRequestCountryEnum,
  SignUpAndOnboardRequestRoleEnum,
  SignUpRequestCountryEnum,
} from '../openapi/yenta';
import { OnboardingSignUpSchema } from '../schemas/OnboardingSignUp.ts';
import ErrorService from '../services/ErrorService.ts';
import { fetchAuthUserDetail, signOutUser } from '../slices/AuthSlice';
import { useAppDispatch } from '../slices/store';
import { AnalyticsEventEnum } from '../types.ts';
import { setAuthCookie } from '../utils/AuthUtils';
import { getCommonsApiErrorMessage } from '../utils/ErrorUtils.ts';
import Logger from '../utils/Logger';
import {
  getKeymakerConfiguration,
  getYentaConfiguration,
} from '../utils/OpenapiConfigurationUtils';

type FormData = z.infer<ReturnType<typeof OnboardingSignUpSchema>>;

const OnboardingSignUpRouteV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const { connectionParticipantId } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const [submitError, setSubmitError] = useState<string>();
  const [isTCError, setIsTCError] = useState<string>('');
  const [onboardingEmail, setOnboardingEmail] = useState<string>('');

  const usernameRefinement = useRefinement<string>(
    async (value): Promise<boolean> => {
      try {
        const { data: username } = await new AuthControllerApi(
          await getKeymakerConfiguration(),
        ).checkUsernameAvailability(value);

        return Boolean(username?.available);
      } catch (e) {
        return false;
      }
    },
    { debounce: 600 },
  );

  const emailRefinement = useRefinement<string>(
    async (value): Promise<boolean> => {
      try {
        const { data: email } = await new AuthControllerApi(
          await getKeymakerConfiguration(),
        ).checkEmailAvailability(value);

        return Boolean(email?.available);
      } catch (e) {
        return false;
      }
    },
    { debounce: 600 },
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(
      OnboardingSignUpSchema({ usernameRefinement, emailRefinement }),
    ),
  });

  const fetchTCUserData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await new PublicControllerApi(
        await getYentaConfiguration(),
      ).getExternalConnectionBasicInfo(connectionParticipantId as string);

      if (!data.expired) {
        setValue('firstName', data.firstName!);
        setValue('lastName', data.lastName!);
        setValue('emailAddress', data.emailAddress!);
        setOnboardingEmail(data.emailAddress!);
      } else {
        setIsTCError('expired');
      }
    } catch (e: any) {
      setIsTCError('invalid');
      Logger.error('Unable to fetch TC onboarding info', e);
      ErrorService.notifyIgnoreHandled('Unable to fetch TC onboarding info', e);
    } finally {
      setLoading(false);
    }
  }, [setValue, connectionParticipantId]);

  useEffect(() => {
    if (connectionParticipantId) {
      fetchTCUserData();
    }
  }, [fetchTCUserData, connectionParticipantId]);

  const onSubmit = async (values: FormData) => {
    try {
      await new PublicControllerApi(
        await getYentaConfiguration(),
      ).userSignupWithOnboarding({
        role: SignUpAndOnboardRequestRoleEnum.TransactionCoordinator,
        firstName: values.firstName,
        lastName: values.lastName,
        username: values.username!,
        country:
          values.country as unknown as SignUpAndOnboardRequestCountryEnum,
        emailAddress: values.emailAddress,
        password: values.password,
      });

      const loginRequest: LoginRequest = {
        usernameOrEmail: values.emailAddress,
        password: values.password,
      };

      const { data } = await new AuthControllerApi(
        await getKeymakerConfiguration(),
      ).authenticateUser(loginRequest);

      setAuthCookie(data?.accessToken!);
      if (data?.userBlockedUntilEmailVerified) {
        navigate('/verify-email');
      } else if (!!data?.forceMfa) {
        navigate('/enable-mfa');
      } else {
        await dispatch(fetchAuthUserDetail());
        navigate('/');
      }
    } catch (e: any) {
      Logger.error('Unable to sign up', e);
      ErrorService.notifyIgnoreHandled('Unable to sign up', e);
      setSubmitError(getCommonsApiErrorMessage(e));
      // this will sign out the TC user when signup is success and onBoarding fails.
      await dispatch(signOutUser());
    }
  };

  if (loading) {
    return (
      <div className='h-screen'>
        <DefaultSplashV2 />;
      </div>
    );
  }

  return (
    <HomeRoute title='One Real: Registration - TCs & Team Admins'>
      <AuthPageLayoutV2
        eventName={AnalyticsEventEnum.SIGNUP_SCREEN_VIEWED}
        rightSection={
          <div className='flex flex-grow justify-center items-start w-full h-full bg-reskin-primary-navy mx-auto'>
            <img
              className='w-full h-screen object-contain'
              src={SignUpLeoImg}
              alt='Turn Listings Into Deals with first AI-Powered Real Estate Platform'
            />
          </div>
        }
      >
        <div className='flex flex-col flex-grow justify-center items-center m-6 lg:mx-auto'>
          <div className='flex flex-col items-center bg-white p-4 md:p-10 gap-6 rounded-xl w-full lg:w-[520px] xl:w-[664px]'>
            <div className='flex flex-col items-center gap-2'>
              <p className='font-inter-regular font-normal text-sm text-center text-reskin-regent-600'>
                Transaction Coordinator & Team Administrator
              </p>
              <p className='font-poppins-semibold font-semibold text-2xl md:text-[32px] leading-normal tracking-[-1px] text-reskin-primary-dark text-center'>
                Create Account
              </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
              {submitError && (
                <div className='mb-5 flex bg-primary-coral rounded-lg p-2'>
                  <p className='font-inter-medium text-white text-base'>
                    {submitError}
                  </p>
                </div>
              )}
              {!isEmpty(isTCError) && (
                <div className='mb-5 flex bg-primary-coral rounded-lg p-2'>
                  <p className='font-inter-medium text-white text-base'>
                    {isTCError === 'invalid' && (
                      <span>
                        Sorry, your invitation cannot be found. Please reach out
                        to{' '}
                        <a
                          className='underline'
                          href={`mailto:${REAL_SUPPORT_LINK}`}
                          target='_top'
                          rel='noreferrer'
                        >
                          support@therealbrokerage.com
                        </a>{' '}
                        if you have questions.
                      </span>
                    )}
                    {isTCError === 'expired' &&
                      'Sorry, your invitation has expired. Please ask your inviting agent to resend the invite.'}
                  </p>
                </div>
              )}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-4 pb-6'>
                <HookFormTextInput<FormData, 'firstName'>
                  name='firstName'
                  control={control}
                  label='First Name'
                  placeholder='Enter first name'
                  shouldUnregister={false}
                  withAsterisk
                />
                <HookFormTextInput<FormData, 'lastName'>
                  name='lastName'
                  control={control}
                  label='Last Name'
                  placeholder='Enter last name'
                  shouldUnregister={false}
                  withAsterisk
                />
                <div className='col-span-1 md:col-span-2'>
                  <HookFormCountrySelect
                    name='country'
                    label='Country'
                    placeholder='Select country'
                    shouldUnregister={false}
                    control={control}
                    countryEnum={SignUpRequestCountryEnum}
                    withAsterisk
                  />
                </div>
                <HookFormTextInput<FormData, 'username'>
                  name='username'
                  control={control}
                  placeholder='eg. johndoe9'
                  label='Username'
                  shouldUnregister={false}
                  onChange={() => usernameRefinement.invalidate()}
                  withAsterisk
                />
                <HookFormEmailInput<FormData, 'emailAddress'>
                  name='emailAddress'
                  control={control}
                  placeholder='johndoe@example.com'
                  label='Email'
                  shouldUnregister={false}
                  disabled={!isEmpty(onboardingEmail)}
                  onChange={() => emailRefinement.invalidate()}
                  withAsterisk
                />
                <HookFormPasswordInput<FormData>
                  placeholder='Choose password'
                  name='password'
                  label='Password'
                  control={control}
                  shouldUnregister={false}
                  withAsterisk
                />
                <HookFormPasswordInput<FormData>
                  placeholder='Confirm password'
                  name='confirmPassword'
                  label='Password Confirmation'
                  control={control}
                  shouldUnregister={false}
                  withAsterisk
                />
              </div>
              <div className='pb-6 space-y-3'>
                <HookFormCheckbox<FormData, 'consentedToTerms'>
                  name='consentedToTerms'
                  control={control}
                  size='sm'
                  data-testid='consentedToTerms'
                  label={
                    <div data-testid='consentedToTerms'>
                      <p className='font-inter-light font-light text-xs text-reskin-grey-600'>
                        By checking the box, I agree to the{' '}
                        <a
                          className='font-inter-bold font-bold'
                          href={TC_ONEREAL_PRIVACY_POLICY_LINK}
                          target='_blank'
                          rel='noreferrer'
                        >
                          Privacy Policy
                        </a>
                        ,{' '}
                        <a
                          className='font-inter-bold font-bold'
                          href={TC_ONEREAL_TERMS_OF_USE_LINK}
                          target='_blank'
                          rel='noreferrer'
                        >
                          Terms of Use{' '}
                        </a>
                        and{' '}
                        <a
                          className='font-inter-bold font-bold'
                          href={TC_ONEREAL_ELECTRONIC_DISCLOSURE_LINK}
                          target='_blank'
                          rel='noreferrer'
                        >
                          Electronic Disclosure{' '}
                        </a>
                        and{' '}
                        <a
                          className='font-inter-bold font-bold'
                          href={TC_ONEREAL_DATA_POLICY_LINK}
                          target='_blank'
                          rel='noreferrer'
                        >
                          Consent
                        </a>
                        .
                      </p>
                    </div>
                  }
                />
                <HookFormCheckbox<FormData, 'consentedToCallMessage'>
                  name='consentedToCallMessage'
                  control={control}
                  size='sm'
                  data-testid='consentedToCallMessage'
                  label={
                    <div data-testid='consentedToCallMessage'>
                      <p className='font-inter-light font-light text-xs text-reskin-grey-600'>
                        By checking the applicable box below, I consent by
                        electronic signature to receive calls and text messages
                        from Real Broker, LLC and its affiliated entities, if
                        the applicable box is checked, made by any means or
                        technology, for informational, marketing, or any other
                        purposes at the phone number I provided above. I
                        understand that my consent to such calls and text
                        messages is not required to purchase products from or
                        use the services of any of the entities listed below.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className='flex flex-col gap-y-2 w-full'>
                <Button
                  type='submit'
                  fullWidth
                  loading={isSubmitting}
                  disabled={!isEmpty(isTCError)}
                  onClick={() => {
                    setSubmitError('');
                  }}
                  rightIcon={faChevronRight}
                >
                  <span className='font-inter text-base font-medium'>
                    Create Account
                  </span>
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  color='secondary'
                  fullWidth
                  onClick={() => navigate('/login')}
                  rightIcon={faChevronRight}
                >
                  <span className='font-inter text-base font-medium'>
                    Already have an Account? Log In
                  </span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      </AuthPageLayoutV2>
    </HomeRoute>
  );
};

export default OnboardingSignUpRouteV2;
